import React, { useCallback, useEffect, useState } from "react";
import { BellRing, CheckCircle2, AlertCircle, Loader2, Send, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { TelegramRules } from "../components/telegram/TelegramRules";

const STEPS = [
  "Dans Telegram, ouvrez @BotFather et envoyez /newbot.",
  "Donnez un nom (ex. « Maison Thaï Alertes »), puis un identifiant finissant par « bot ».",
  "BotFather vous donne une clé : enregistrez-la dans la fenêtre sécurisée proposée par votre assistant AgroManage.",
  "Ouvrez votre nouveau robot dans Telegram et appuyez sur « Démarrer ».",
  "Revenez ici et cliquez « Relier mon Telegram ».",
];

function StatusLine({ status }) {
  if (!status.configured) {
    return <p className="text-sm text-[#A04E2C] flex gap-2" data-testid="telegram-status-missing"><AlertCircle className="w-4 h-4 mt-0.5" />Clé du robot Telegram pas encore enregistrée.</p>;
  }
  if (!status.linked) {
    return <p className="text-sm text-[#8C6B2F] flex gap-2" data-testid="telegram-status-unlinked"><AlertCircle className="w-4 h-4 mt-0.5" />Robot prêt, téléphone pas encore relié.</p>;
  }
  return (
    <p className="text-sm text-[#1F4E3D] flex gap-2" data-testid="telegram-status-linked">
      <CheckCircle2 className="w-4 h-4 mt-0.5" />Alertes envoyées à {status.chat_name || "votre Telegram"}{status.bot_username && <> via @{status.bot_username}</>}.
    </p>
  );
}

export default function TelegramAlertSettings() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get("/admin/telegram-alert/status").then(({ data }) => setStatus(data)), []);
  useEffect(() => { load().catch(() => setStatus({ configured: false, linked: false })); }, [load]);

  const run = async (action) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/admin/telegram-alert/${action}`);
      toast.success(action === "link" ? `Telegram relié : ${data.chat_name || "OK"}` : "Message de test envoyé, regardez votre téléphone");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  if (!status) return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1F4E3D]" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 space-y-6" data-testid="telegram-settings-page">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#EAF3EE] flex items-center justify-center"><BellRing className="w-5 h-5 text-[#1F4E3D]" /></div>
        <div>
          <h1 className="font-heading text-2xl font-medium text-[#1A1C1A]">Alerte Telegram</h1>
          <p className="text-sm text-[#575A57]">Un message sur votre téléphone à chaque commande web payée par PayPal, aux écarts de réassort suspects et chaque soir.</p>
        </div>
      </div>
      <div className="bg-white border border-[#E5E5E0] rounded-xl p-5 space-y-4">
        <StatusLine status={status} />
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => run("link")} disabled={!status.configured || !!busy} className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white" data-testid="telegram-link-btn">
            {busy === "link" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}Relier mon Telegram
          </Button>
          <Button variant="outline" onClick={() => run("test")} disabled={!status.linked || !!busy} data-testid="telegram-test-btn">
            {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Envoyer un test
          </Button>
        </div>
      </div>
      <TelegramRules linked={status.linked} />
      <div className="bg-[#FAFAF7] border border-[#E5E5E0] rounded-xl p-5">
        <h2 className="font-heading text-base font-medium mb-2">Mise en place (2 minutes, une seule fois)</h2>
        <ol className="list-decimal pl-5 space-y-1 text-sm text-[#1A1C1A]" data-testid="telegram-steps">
          {STEPS.map((s) => <li key={s}>{s}</li>)}
        </ol>
      </div>
    </div>
  );
}
